import { apiClient } from './client'
import {
  ListOrdersResponseSchema,
  OpenOrdersCountResponseSchema,
  OrderSchema,
  OrderMessagesResponseSchema,
  OrderRefundStateSchema,
  type OpenOrdersCountResponse,
  type Order,
  type OrderMessage,
  type OrderRefundState,
  type OrderShelf,
  type OrderStatus,
  type OrderActionStatus,
  type RefundOrderRequest,
} from '@random-vinyl/backend/schemas'

/** Query params for GET /orders. Omit `shelf` for the default `open` queue. */
export interface ListOrdersParams {
  shelf?: OrderShelf
  status?: OrderStatus
  limit?: number
  cursor?: string
}

export const ordersService = {
  /** One page of orders (newest activity first). Paginate via the returned `nextCursor`. */
  async listPage(params: ListOrdersParams = {}): Promise<{ items: Order[]; nextCursor?: string }> {
    const raw = await apiClient('/orders', { query: params })
    return ListOrdersResponseSchema.parse(raw)
  },

  /** Open-queue size for the nav badge/KPI — `actionable` excludes awaiting_payment. */
  async openCount(): Promise<OpenOrdersCountResponse> {
    const raw = await apiClient('/orders/open-count')
    return OpenOrdersCountResponseSchema.parse(raw)
  },

  /**
   * Find ONE order by whatever the customer read out over the phone: the human
   * order number from their confirmation mail (`RV-2026-0834`) or the
   * technical id from a link or an audit row. The server tells the two apart
   * by shape. Throws a 404-shaped error when nothing matches.
   */
  async lookup(q: string): Promise<Order> {
    const raw = await apiClient('/orders/lookup', { query: { q } })
    return OrderSchema.parse(raw)
  },

  async get(channelOrderId: string): Promise<Order> {
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}`)
    return OrderSchema.parse(raw)
  },

  /** The live buyer↔seller thread (fetched fresh from the channel each call). */
  async messages(channelOrderId: string): Promise<OrderMessage[]> {
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}/messages`)
    return OrderMessagesResponseSchema.parse(raw).items
  },

  /**
   * Send a buyer message, optionally changing status in the same action.
   * `requestId` keys server-side idempotency — the message post is non-idempotent,
   * so a repeated requestId replays the stored order instead of re-messaging the buyer.
   */
  async sendMessage(
    channelOrderId: string,
    body: string,
    status: OrderActionStatus | undefined,
    requestId: string
  ): Promise<Order> {
    const payload: { body: string; status?: OrderActionStatus; requestId: string } = {
      body,
      requestId,
    }
    if (status) payload.status = status
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}/messages`, {
      method: 'POST',
      body: payload,
    })
    return OrderSchema.parse(raw)
  },

  /** Change status. `requestId` keys server-side idempotency (the status post is non-idempotent). */
  async updateStatus(
    channelOrderId: string,
    status: OrderActionStatus,
    requestId: string
  ): Promise<Order> {
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}/status`, {
      method: 'POST',
      body: { status, requestId },
    })
    return OrderSchema.parse(raw)
  },

  /**
   * What this order has had refunded, and what may still be refunded.
   *
   * Its own call rather than fields on the order: a Discogs order row is rebuilt
   * from their payload on every poll and cannot carry refund state, so the
   * numbers are derived server-side from the refund rows. The per-line caps here
   * ARE the server's caps — the panel must never offer what the API refuses.
   */
  async refundState(channelOrderId: string): Promise<OrderRefundState> {
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}/refunds`)
    return OrderRefundStateSchema.parse(raw)
  },

  /**
   * Refund an order, in full or line by line.
   *
   * For a webshop order this MOVES MONEY through Mollie; for a Discogs one the
   * seller has already refunded on Discogs and this only records it. `requestId`
   * keys server-side idempotency — a retry replays instead of refunding twice.
   *
   * Returns the order, which for a Discogs order is deliberately unchanged: read
   * `refundState` again for the new totals.
   */
  async refund(channelOrderId: string, body: RefundOrderRequest): Promise<Order> {
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}/refund`, {
      method: 'POST',
      body,
    })
    return OrderSchema.parse(raw)
  },

  /** Set the seller-only internal note (never sent to the buyer). */
  async setNote(channelOrderId: string, note: string | null): Promise<Order> {
    const raw = await apiClient(`/orders/${encodeURIComponent(channelOrderId)}/note`, {
      method: 'POST',
      body: { note },
    })
    return OrderSchema.parse(raw)
  },
}
